// @givmo/compliance — the form's own fee chart, under the fee it helps answer (CCM-R1-DOCS-PRINTED-WEB;
// slice 2 §5.4, frame 34, approved as shown).
//
// ⚠ EVERY WORD IS THE FORM'S OR THE FRAME'S. The rows, the notes and the column headings are the
// form's print, served by the catalog (CCM-R1-FORM-CATALOG, its shape CCM-R1-FORM-CATALOG-B's); the
// caption is frame 34's, its page the form's own. No band is chosen and nothing is worked out from
// the figures: they are the government's, shown as printed (§3.3 row 20). Frame 34 draws no column
// headings, so they sit in the table's head for assistive technology alone.

import { testIdProp } from '@givmo/design-system';
import type { FormFeeChart } from './compliance-preparation-decode';
import {
  AS_PRINTED,
  feeChartCaption,
  feeChartNoteLine,
  printsAMalformedFigure,
} from './compliance-preparation-model';

export function FeeChart({
  chart,
  jurisdiction,
  caption,
  testId,
}: {
  chart: FormFeeChart;
  /** The workbench's: frame 34's caption names the state, the form being on screen. */
  jurisdiction?: string;
  /** Elsewhere, the caption whole, the form named (the review's online question, cut 1008-92). */
  caption?: string;
  testId: string;
}) {
  return (
    <div
      className="mt-4 flex min-w-0 flex-col gap-6 rounded-lg bg-surface-sunken px-16 py-12"
      {...testIdProp(testId)}
    >
      <table className="w-full border-collapse text-s12 leading-h15">
        <caption className="pb-4 text-left text-s12 font-w600 text-text-secondary">
          {caption ?? feeChartCaption(jurisdiction ?? '', chart.page)}
        </caption>
        <thead className="sr-only">
          <tr>
            {chart.headings.map((heading, index) => (
              <th key={index} scope="col">
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {chart.rows.map((row, rowIndex) => (
            <tr key={rowIndex} {...testIdProp(`${testId}-row`)}>
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={
                    cellIndex === 0
                      ? 'py-[1px] pr-12 text-left align-top text-text-secondary'
                      : 'py-[1px] pl-12 text-right align-top whitespace-nowrap text-text-secondary tabular-nums'
                  }
                >
                  {cell}
                  {printsAMalformedFigure(cell) && (
                    <span className="text-text-tertiary"> {AS_PRINTED}</span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {chart.notes.map((note, index) => (
        <p
          key={index}
          className="text-s12 leading-h15 text-text-tertiary"
          {...testIdProp(`${testId}-note`)}
        >
          {feeChartNoteLine(note.text, chart.rows)}
        </p>
      ))}
    </div>
  );
}
